import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { Trash2, AlertTriangle, ArrowLeft, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { authService } from '../services/auth';
import toast from 'react-hot-toast';

const DeleteAccountPage = () => {
    const [loading, setLoading] = useState(false);
    const [deleted, setDeleted] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [confirmed, setConfirmed] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm();

    const onSubmit = async (data) => {
        if (!confirmed) {
            toast.error('Debes confirmar que entiendes que esta acción es irreversible');
            return;
        }

        try {
            setLoading(true);
            await authService.deleteAccount(data.email, data.password);
            // Limpiar sesión local si la hubiera
            authService.logout();
            setDeleted(true);
        } catch (err) {
            const message = err?.response?.data?.detail || 'Error al eliminar la cuenta. Verifica tus credenciales.';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    if (deleted) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md w-full space-y-8">
                    <div className="text-center">
                        <h1 className="text-4xl font-bold text-blue-600 mb-2">ARGUS</h1>
                        <div className="bg-white p-8 rounded-lg shadow-md">
                            <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                                <CheckCircle className="h-8 w-8 text-green-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">Cuenta Eliminada</h2>
                            <p className="text-gray-600 mb-6">
                                Tu cuenta y todos los datos asociados han sido eliminados permanentemente de nuestros sistemas.
                            </p>
                            <Link
                                to="/login"
                                className="inline-flex items-center text-blue-600 hover:text-blue-500"
                            >
                                <ArrowLeft className="h-4 w-4 mr-2" />
                                Volver al inicio
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div>
                    <h1 className="text-center text-4xl font-bold text-blue-600 mb-2">Argus</h1>
                    <h2 className="text-center text-2xl font-bold text-gray-900">
                        Eliminar Cuenta
                    </h2>
                    <p className="mt-2 text-center text-sm text-gray-600">
                        Esta acción eliminará permanentemente tu cuenta y todos los datos asociados
                    </p>
                </div>

                {/* Aviso de advertencia */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                        <div>
                            <h3 className="text-sm font-semibold text-red-800 mb-1">
                                Acción irreversible
                            </h3>
                            <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
                                <li>Tu perfil y datos personales serán eliminados</li>
                                <li>No podrás recuperar tu cuenta ni historial</li>
                                <li>Esta acción no se puede deshacer</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <form className="mt-4 space-y-6" onSubmit={handleSubmit(onSubmit)}>
                    <div className="bg-white p-8 rounded-lg shadow-md space-y-4">
                        {/* Email */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Correo electrónico
                            </label>
                            <input
                                {...register('email', {
                                    required: 'El email es obligatorio',
                                    pattern: {
                                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                        message: 'Email inválido'
                                    }
                                })}
                                type="email"
                                placeholder="tu@email.com"
                                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                                    errors.email ? 'border-red-500' : 'border-gray-300'
                                }`}
                            />
                            {errors.email && (
                                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                            )}
                        </div>

                        {/* Contraseña */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Contraseña
                            </label>
                            <div className="relative">
                                <input
                                    {...register('password', {
                                        required: 'La contraseña es obligatoria'
                                    })}
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Tu contraseña actual"
                                    className={`w-full px-4 py-3 pr-12 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                                        errors.password ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
                                >
                                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                </button>
                            </div>
                            {errors.password && (
                                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                            )}
                        </div>

                        {/* Checkbox de confirmación */}
                        <div className="flex items-start gap-3 pt-2">
                            <input
                                type="checkbox"
                                id="confirm-delete"
                                checked={confirmed}
                                onChange={(e) => setConfirmed(e.target.checked)}
                                className="mt-1 h-4 w-4 text-red-600 border-gray-300 rounded cursor-pointer"
                            />
                            <label htmlFor="confirm-delete" className="text-sm text-gray-700 cursor-pointer">
                                Entiendo que esta acción es <strong>permanente e irreversible</strong> y que todos mis datos serán eliminados
                            </label>
                        </div>

                        {/* Botón eliminar */}
                        <div className="pt-2">
                            <button
                                type="submit"
                                disabled={loading || !confirmed}
                                className={`w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-semibold text-white transition-colors ${
                                    loading || !confirmed
                                        ? 'bg-red-300 cursor-not-allowed'
                                        : 'bg-red-600 hover:bg-red-700 active:bg-red-800'
                                }`}
                            >
                                {loading ? (
                                    <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : (
                                    <Trash2 className="h-5 w-5" />
                                )}
                                {loading ? 'Eliminando cuenta...' : 'Eliminar mi cuenta permanentemente'}
                            </button>
                        </div>

                        <div className="text-center pt-2">
                            <Link
                                to="/login"
                                className="inline-flex items-center text-sm text-blue-600 hover:text-blue-500"
                            >
                                <ArrowLeft className="h-4 w-4 mr-2" />
                                Cancelar y volver al login
                            </Link>
                        </div>
                    </div>
                </form>

                <p className="text-center text-xs text-gray-500">
                    Si tienes problemas para eliminar tu cuenta, contáctanos a{' '}
                    <a href="mailto:soporte@argus.com" className="text-blue-600 hover:underline">
                        soporte@argus.com
                    </a>
                </p>
            </div>
        </div>
    );
};

export default DeleteAccountPage;
